import { keepPreviousData } from '@tanstack/react-query';
import useGetApi from 'src/api/useGetApi';
import { InboundEmailDTO } from 'src/types/application/InboundEmailDTO';
// AR Technical Demo (F0 / GEN-2256) — demo-mode shim. Delete with frontend/src/demo/ar.
import {
  arDemoActive,
  useArDemoFixture,
  withArDemoData,
} from '@/demo/ar/hook-shim';
import type { DemoInboundEmail } from '@/demo/ar/fixture-store.types';

interface UseGetInboundEmailParams {
  inboundEmailId: string;
  enabled?: boolean;
  includeSplitDocuments?: boolean;
}

/**
 * Map the RPC-shaped demo inbound email (stored in the fixture store for
 * provenance) into the legacy `InboundEmailDTO` this endpoint serves, so the
 * source-email viewer (`InboundEmailViewer`) renders it unchanged in demo mode.
 */
function demoEmailToDto(email: DemoInboundEmail): InboundEmailDTO {
  return {
    id: email.id,
    messageId: email.id,
    from: email.from ?? '',
    to: email.to,
    cc: email.cc ?? null,
    subject: email.subject ?? null,
    textContent: email.textContent ?? null,
    htmlContent: email.htmlContent ?? null,
    service: 'microsoft-graph-webhook',
    attachmentCount: email.attachments?.length ?? 0,
    createdAt: email.createdAt,
    updatedAt: email.createdAt,
    conversationId: email.conversationId ?? undefined,
    attachments: (email.attachments ?? []).map((a) => ({
      id: a.id,
      originalName: a.fileName,
      s3Url: a.s3Url,
      mimeType: a.mimeType ?? 'application/octet-stream',
      sizeBytes: a.sizeBytes ?? 0,
      contentId: a.contentId ?? null,
    })),
  };
}

/**
 * Split-child documents (documents with a parentDocumentId) are excluded
 * from the email's attachments; pass includeSplitDocuments to also fetch
 * them in splitAttachments.
 */
const useGetInboundEmail = ({
  inboundEmailId,
  enabled = true,
  includeSplitDocuments = false,
}: UseGetInboundEmailParams) => {
  const demoData = useArDemoFixture((s) => {
    const email = s.inboundEmailsById[inboundEmailId];
    return email ? demoEmailToDto(email) : undefined;
  });
  const baseQuery = useGetApi<InboundEmailDTO>({
    endpoint: `communications/inbound-emails/${inboundEmailId}`,
    queryParameters: includeSplitDocuments
      ? { includeSplitDocuments: true }
      : undefined,
    options: {
      enabled: enabled && !!inboundEmailId && !arDemoActive(),
      // Keep showing the already-loaded email while the split-documents
      // refetch is in flight (the flag changes the query key).
      placeholderData: includeSplitDocuments ? keepPreviousData : undefined,
    },
  });
  return withArDemoData(baseQuery, demoData);
};

export default useGetInboundEmail;
